import type { BenchmarkEntry } from "./benchmarkQuestion.context"
import type { BenchmarkDistribution } from "./benchmarkQuestion.types"

/**
 * Delta between a score and a benchmark value, both expressed in raw domain units.
 * `scale` converts raw → displayed units (10 for 0-100/0-10 scale questions; 1 for NPS,
 * which is already displayed in its raw -100..100 units). `decimals` is the DISPLAY precision
 * (1 for scale questions' "X.X"; 0 for NPS, which rounds to whole points like the score card).
 * Rounds each side to `decimals` BEFORE subtracting, so the result always matches what a user
 * gets by subtracting the two displayed numbers (e.g. "Moy. 8.2" - "Vous 7.6") — avoids the
 * classic "-0.5 shown but 8.2-7.6=0.6" complaint.
 */
export function benchmarkDelta(score: number, benchmarkValue: number, scale = 10, decimals = 1): number {
  const rounded = (n: number) => parseFloat((n / scale).toFixed(decimals))
  return parseFloat((rounded(score) - rounded(benchmarkValue)).toFixed(decimals))
}

export interface NpsBenchmarkEntry {
  value: number
  count: number
  // Label to display: the account's own sector when matched, "all" when it fell back but the
  // account does have a sector (worth saying "compared against all sectors"), null only when the
  // account itself has no sector at all — showing "all" there would misleadingly suggest a real
  // comparison happened when the actual problem is the account was never assigned one.
  sector: string | null
  distribution: BenchmarkDistribution | null
}

interface NpsBenchmarkResponse {
  sector?: string | null
  benchmarks?: any[]
}

/**
 * Resolve the single NPS benchmark row to compare against, from a /benchmark/nps response.
 * Same sector→all fallback as buildBenchmarkByUuid, but for the one NPS row rather than one
 * row per canonical question (the 'nps' type has no question_aid grouping).
 */
export function resolveNpsBenchmark(response: NpsBenchmarkResponse): NpsBenchmarkEntry | null {
  const wantedSector = response.sector ?? null
  const rows = Array.isArray(response.benchmarks) ? response.benchmarks.filter((b) => b.size === "all") : []
  const bySector = wantedSector ? rows.find((b) => b.sector === wantedSector) : null
  const byAll = rows.find((b) => b.sector === "all")
  const row = bySector ?? byAll
  if (!row) return null
  return {
    value: parseFloat(row.value),
    count: row.count ?? 0,
    sector: wantedSector ? (bySector ? wantedSector : "all") : null,
    distribution: row.distribution ?? null
  }
}

interface BenchmarkResponse {
  bankBenchmarks?: any[]
  bankIdMap?: any[]
  sector?: string | null
}

/**
 * Build a UUID-keyed benchmark map from a benchmarkFetchQuestions response.
 * Prefers the account's own sector row per canonical question; falls back to the all/all
 * row when that specific question has no sector-specific data (not enough accounts in that
 * sector) — the entry's `sector` field reflects whichever was actually used: the account's own
 * sector when matched, "all" when it fell back but the account does have one, null only when the
 * account itself has no sector at all (2026-09-22 — was collapsing both fallback reasons into
 * null, hiding "compared against all sectors" even when that's a real, useful thing to say).
 * Phase 2 (2026-09-11) — keyed by bank_id instead of question_aid; entry.canonicalAid now holds
 * a bank_id (validated against the legacy question_aid path: same 62,401 live questions covered,
 * same values bar one already-understood merge — see KB features/bank-questions-vision.md §19).
 */
export function buildBenchmarkByUuid(response: BenchmarkResponse): Record<string, BenchmarkEntry> {
  const wantedSector = response.sector ?? null
  const bySector: Record<string, { value: number; count: number }> = {}
  const byAll: Record<string, { value: number; count: number }> = {}

  if (Array.isArray(response.bankBenchmarks)) {
    response.bankBenchmarks.forEach((b) => {
      if (b.size !== "all" || !b.bank_id) return
      const key = String(b.bank_id)
      if (b.sector === "all") byAll[key] = { value: parseFloat(b.value), count: b.count ?? 0 }
      if (wantedSector && b.sector === wantedSector) bySector[key] = { value: parseFloat(b.value), count: b.count ?? 0 }
    })
  }

  const map: Record<string, BenchmarkEntry> = {}
  if (Array.isArray(response.bankIdMap)) {
    response.bankIdMap.forEach((m) => {
      const key = String(m.bank_id)
      const matched = bySector[key] ?? byAll[key]
      if (!matched) return
      map[m.app_question_id] = {
        ...matched,
        canonicalAid: key,
        // Same fix as resolveNpsBenchmark above, 2026-09-22 — "all" (fell back, but the account
        // does have a sector) is now distinct from null (the account has none at all), instead
        // of collapsing both into null.
        sector: wantedSector ? (bySector[key] ? wantedSector : "all") : null
      }
    })
  }
  return map
}
